import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Car, CheckCircle, ChevronDown, ChevronRight, ChevronUp, CircleDot, Clock, Euro, Info, Mail, MapPin, MessageSquare, Power, Star, Undo2, UserX, XCircle } from 'lucide-react';
import { SkeletonPage, Skeleton, CHART, chartTooltip } from '../ui/kit';
// ZEIT-EINHEIT-PORTAL-001: Server-Zeiten sind UTC — angezeigt wird ausschliesslich ueber diesen Baustein.
import { berlinerBetriebswoche, betriebstagSchluessel, dauer, tagUeberschrift, uhrzeit } from '../lib/zeit';

// Dieselbe Hausform wie die Leistung-Seite (Performance.js:6-14): ein NICHT ERFASSTER Wert
// ist weder null Euro noch null Fahrten — er bekommt „–". Sonst liest der Partner
// „nichts verdient", wo in Wahrheit „nichts gemessen" steht. Die Route liefert fuer
// `earnings_per_hour`, `utilization` und `trips_per_hour` seit PARTNER-KLARSPRACHE-SALDEN-001
// genau dann `null`, wenn fuer den Zeitraum keine Dienstzeit protokolliert ist
// (backend/src/routes/partners.js:3411-3435) — unbedingtes `.toFixed()` liess die ganze
// Fahrerakte darauf abstuerzen, nicht nur eine Kachel.
const fmtEur = (v) => (v == null
  ? '–'
  : `€${v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtNum = (v, d = 2) => (v == null
  ? '–'
  : v.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtPct = (v, d = 1) => (v == null ? '–' : `${v.toFixed(d)}%`);

function defaultRange() {
  // Dieselbe zentrale Berliner Kalenderwoche wie in Leistung und Einnahmen. Der Browser
  // kann sich auf Reisen in einer anderen Zeitzone befinden; die Abrechnung bleibt Berlin.
  return berlinerBetriebswoche();
}

const TABS = [
  { id: 'performance', label: 'Leistung' },
  { id: 'requirements', label: 'Zuweisungen & Anforderungen' },
  { id: 'earnings', label: 'Einnahmen' },
  { id: 'activity', label: 'Fahrer-Aktivität' },
];

export default function DriverProfile() {
  const { id } = useParams();
  const [tab, setTab] = useState('performance');
  const [data, setData] = useState(null);
  const [range, setRange] = useState(defaultRange());
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/partners/driver/${id}`, { params: { from: range.from, to: range.to } })
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id, range]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <SkeletonPage />;
  if (!data) return (
    <div style={{ padding: 40 }}>
      <div style={{ color: '#6b7280', marginBottom: 12 }}>Fahrer nicht gefunden</div>
      <Link to="/drivers" style={{ color: '#000', fontWeight: 700 }}>← Zurück zu Fahrer</Link>
    </div>
  );

  const d = data.driver;

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 18 }}>
        <Link to="/drivers" style={{ color: '#000', textDecoration: 'underline', fontWeight: 600 }}>Fahrer</Link>
        <span style={{ margin: '0 8px' }}>›</span> Fahrer-Profil
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800 }}>{d.name?.charAt(0)}</div>
          <div>
            <div style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, display: 'flex', alignItems: 'center', gap: 8 }}>{d.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 700, color: '#9C27B0' }}>
                <CheckCircle size={14} /> {d.approval_status === 'approved' ? 'Aktiv' : d.approval_status === 'pending' ? 'In Prüfung' : 'Abgelehnt'}
              </span>
              <span style={{ fontSize: 13, color: '#4b5563' }}>· {d.is_online ? 'Online' : 'Offline'}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={`mailto:${d.email}`} style={{ width: 40, height: 40, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}><Mail size={17} /></a>
          <button style={{ width: 40, height: 40, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151', border: 'none', cursor: 'pointer' }}><MessageSquare size={17} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: '12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: tab === t.id ? '#000' : '#6b7280', borderBottom: tab === t.id ? '3px solid #000' : '3px solid transparent', marginBottom: -1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'performance' && <PerformanceTab data={data} range={range} setRange={setRange} />}
      {tab === 'requirements' && <RequirementsTab data={data} />}
      {tab === 'earnings' && <EarningsTab id={id} range={range} setRange={setRange} />}
      {tab === 'activity' && <ActivityTab id={id} range={range} setRange={setRange} />}
    </div>
  );
}

// `hinweis` ist die Erklaerzeile einer Kennzahl, die sich nicht von selbst versteht —
// dieselbe Aufgabe wie die Spaltenerklaerungen auf der Leistung-Seite (Performance.js:66-77).
function MetricCard({ icon, title, big, bigSub, small, smallSub, hinweis }) {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{icon} {title}</div>
      <div style={{ fontSize: 22, fontWeight: 900 }}>{big}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{bigSub}</div>
      <div style={{ fontSize: 22, fontWeight: 900 }}>{small}</div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{smallSub}</div>
      {hinweis && (
        <div style={{ display: 'flex', gap: 5, alignItems: 'flex-start', marginTop: 8, fontSize: 11, color: '#4b5563', lineHeight: 1.4 }}>
          <Info size={12} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{hinweis}</span>
        </div>
      )}
    </div>
  );
}

function PerformanceTab({ data, range, setRange }) {
  const p = data.performance;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ fontSize: 16, fontWeight: 800 }}>Leistungsdaten</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 12 }} />
          <span style={{ color: '#6b7280' }}>–</span>
          <input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 12 }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
        <MetricCard icon={<Euro size={16} />} title="Partnerumsatz" big={fmtEur(p.earnings)} bigSub="keine Auszahlung" small={fmtEur(p.earnings_per_hour)} smallSub="Partnerumsatz / Std" />
        {/* „Stunden online" heisst die Zahl auf der Leistung-Seite (Performance.js:71) — hier hiess
            dieselbe Zahl „Online-Stunden". Zwei Namen fuer eine Zahl sind schlimmer als ein
            unschoener Name, deshalb steht ueberall derselbe. */}
        <MetricCard icon={<Clock size={16} />} title="Stunden online" big={fmtNum(p.hours_online, 2)} bigSub="Stunden online"
          small={fmtPct(p.utilization)} smallSub="Auslastung"
          hinweis="Anteil der Stunden online, den der Fahrer am Steuer verbracht hat — der Rest ist dienstbereites Warten." />
        <MetricCard icon={<MapPin size={16} />} title="Fahrten" big={p.trips} bigSub="Fahrten" small={fmtNum(p.trips_per_hour, 2)} smallSub="Fahrten pro Std" />
        <MetricCard icon={<Star size={16} />} title="Qualität" big={fmtPct(p.acceptance)} bigSub="Annahmequote" small={fmtPct(p.cancellation)} smallSub="Stornoquote" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Partnerumsatz · keine Auszahlung</h4>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data.daily}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={CHART.grid} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: CHART.axis }} tickFormatter={d => d?.slice(5)} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: CHART.axis }} />
                <Tooltip formatter={v => [`${Number(v).toFixed(2)} €`, 'Partnerumsatz']} contentStyle={chartTooltip} />
                <Line type="monotone" dataKey="earnings" stroke={CHART.ink} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Fahrten pro Tag</h4>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data.daily}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={CHART.grid} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: CHART.axis }} tickFormatter={d => d?.slice(5)} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: CHART.axis }} allowDecimals={false} />
                <Tooltip formatter={v => [v, 'Fahrten']} contentStyle={chartTooltip} />
                <Line type="monotone" dataKey="trips" stroke={CHART.ink} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

const DRIVER_REQS = [
  { type: 'terms', label: 'Geschäftsbedingungen' },
  { type: 'fuehrerschein', label: 'Führerschein' },
  { type: 'ausweis', label: 'Personalausweis / Aufenthaltstitel / Reisepass' },
  { type: 'pschein', label: 'Personenbeförderungsschein (P-Schein)' },
  { type: 'profilbild', label: 'Profilbild' },
];

function RequirementsTab({ data }) {
  const docs = data.documents || [];
  const activeVehicle = data.vehicle;
  // Additiver API-Vertrag: alte Backends liefern nur `vehicle`; neue liefern die
  // vollstaendige Berechtigungsmenge. Die aktive Wahl bleibt davon getrennt.
  const authorizedVehicles = Array.isArray(data.authorized_vehicles)
    ? data.authorized_vehicles
    : (activeVehicle ? [{ ...activeVehicle, is_active: true }] : []);
  const vehicles = activeVehicle && !authorizedVehicles.some(v => v.id === activeVehicle.id)
    ? [{ ...activeVehicle, is_active: true }, ...authorizedVehicles]
    : authorizedVehicles;
  const badge = {
    approved: { icon: <CheckCircle size={14} color="#9C27B0" />, label: 'Abgeschlossen', color: '#9C27B0' },
    pending: { icon: <Clock size={14} color="#4b5563" />, label: 'In Prüfung', color: '#4b5563' },
    rejected: { icon: <XCircle size={14} color="#dc2626" />, label: 'Abgelehnt', color: '#dc2626' },
    expired: { icon: <AlertTriangle size={14} color="#dc2626" />, label: 'Abgelaufen', color: '#dc2626' },
    missing: { icon: <Clock size={14} color="#6b7280" />, label: 'Ausstehend', color: '#6b7280' },
  };
  const matchesType = (doc, type) => String(doc.document_type || '').replace(/_/g, '').includes(type);
  const statusOfType = (rows) => {
    if (rows.some(x => Number(x.is_effective) === 1 && x.status === 'approved')) return 'approved';
    const latest = [...rows].sort((a, b) => Number(b.revision || 0) - Number(a.revision || 0))[0];
    if (!latest || latest.status === 'superseded') return 'missing';
    return badge[latest.status] ? latest.status : 'missing';
  };
  const statusOf = (type) => {
    const slot = docs.filter(x => matchesType(x, type));
    if (!slot.length) return data.driver.approval_status === 'approved' ? 'approved' : 'missing';
    const byType = new Map();
    slot.forEach(x => { const key = x.document_type; byType.set(key, [...(byType.get(key) || []), x]); });
    const states = [...byType.values()].map(statusOfType);
    for (const worst of ['rejected', 'expired', 'pending', 'missing']) if (states.includes(worst)) return worst;
    return 'approved';
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 16 }}>Fahrer-Anforderungen</h3>
        {DRIVER_REQS.map(req => {
          const b = badge[statusOf(req.type)] || badge.missing;
          return (
            <div key={req.type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{req.label}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, fontSize: 12, fontWeight: 600, color: b.color }}>{b.icon} {b.label}</div>
              </div>
              <ChevronRight size={16} color="#6b7280" />
            </div>
          );
        })}
      </div>
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 16 }}>Fahrzeug-Zuweisungen</h3>
        {vehicles.length ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {vehicles.map(v => {
              const isActive = v.id === activeVehicle?.id || v.is_active === true;
              return (
                <div key={v.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Car size={20} color="#4b5563" /></div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{v.brand} {v.model} {v.year ? `(${v.year})` : ''}</div>
                    <div style={{ fontSize: 13, color: '#4b5563' }}>{v.license_plate} {v.color ? `· ${v.color}` : ''}</div>
                    <div style={{ marginTop: 5, fontSize: 12, fontWeight: 700, color: isActive ? '#15803d' : '#6b7280' }}>
                      {isActive ? 'Aktiv gewählt' : 'Zugewiesen, in der App wählbar'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ border: '1px dashed #d1d5db', borderRadius: 12, padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Kein Fahrzeug zugewiesen oder zur Auswahl freigegeben</div>
        )}
      </div>
    </div>
  );
}

function EarningsTab({ id, range, setRange }) {
  const [e, setE] = useState(null);
  useEffect(() => {
    api.get(`/partners/driver/${id}/earnings`, { params: { from: range.from, to: range.to } }).then(r => setE(r.data)).catch(() => {});
  }, [id, range]);
  if (!e) return <Skeleton h={80} r={10} />;
  const b = e.breakdown || {};
  const Row = ({ label, value, bold }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: bold ? 'none' : '1px solid #f3f4f6' }}>
        <span style={{ fontSize: 13, fontWeight: bold ? 800 : 500 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: bold ? 800 : 600 }}>{value}</span>
      </div>
  );
  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <input type="date" value={range.from} onChange={ev => setRange(r => ({ ...r, from: ev.target.value }))} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13 }} />
        <span style={{ color: '#6b7280' }}>–</span>
        <input type="date" value={range.to} onChange={ev => setRange(r => ({ ...r, to: ev.target.value }))} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13 }} />
      </div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '4px 18px' }}>
        <div style={{ fontSize: 12, color: '#4b5563', padding: '12px 0 4px' }}>
          Informative Zuordnung aus Buchungen – keine Rechnung und keine Auszahlung.
        </div>
        <Row label="Partnerumsatz" value={fmtEur(e.total_earnings)} bold />
        <Row label="Korrekturen" value={fmtEur(e.adjustments)} />
        <Row label="Trinkgeld – Information" value={fmtEur(b.tips)} />
        <Row label="Bonus – Partner → Fahrer" value={fmtEur(b.bonus_information)} />
      </div>
      <div style={{ display: 'flex', gap: 40, marginTop: 20 }}>
        {/* PARTNER-KLARSPRACHE-SALDEN-001: `b.trips` zaehlt JEDE Endbuchung einer Fahrt
              (financeLedger.js:14, TERMINAL_RIDE_EVENT_TYPES), also auch Stornogebuehr und
              Nichtantritt — der Reiter „Leistung" nebenan zaehlt nur `status='completed'`.
              Beide „Fahrten" zu nennen liess dieselbe Beschriftung zwei verschiedene Zahlen
              tragen, zwei Klicks auseinander. Wortlaut wie auf der Einnahmen-Seite. */}
          <div><div style={{ fontSize: 12, color: '#6b7280' }}>Abgeschlossene Fahrten, Storno und Nichtantritt</div><div style={{ fontSize: 22, fontWeight: 800 }}>{b.trips || 0}</div></div>
      </div>
    </div>
  );
}

// ── FAHRER-AKTIVITAET-UBER-001 ───────────────────────────────────────────────
// Der Tagesverlauf eines Fahrers als durchgehende Kette: wann er online ging, was
// er angenommen, abgelehnt, gefahren oder storniert hat — und wie lange zwischen
// zwei Schritten vergangen ist.
//
// Alle Zeiten kommen als ISO mit `Z` vom Server und werden ausschließlich über
// lib/zeit.js angezeigt (ZEIT-EINHEIT-PORTAL-001). Gruppiert wird nach BERLINER
// Kalendertag, nicht nach UTC — sonst zerschneidet die Gruppierung den Abend.

// Aussehen je Ereignisart. `rail` ist die Farbe des Knotens auf der Linie.
const EREIGNIS = {
  online:           { rail: '#16a34a', icon: Power,       stark: true },
  offline:          { rail: '#0a0a0a', icon: Power,       stark: true },
  accepted:         { rail: '#4b5563', icon: CheckCircle },
  arrived:          { rail: '#4b5563', icon: MapPin },
  trip_started:     { rail: '#9C27B0', icon: Car,         betont: true },
  trip_completed:   { rail: '#4b5563', icon: CheckCircle },
  cancelled_driver: { rail: '#dc2626', icon: XCircle },
  cancelled:        { rail: '#dc2626', icon: XCircle },
  no_show:          { rail: '#dc2626', icon: UserX },
  offer_declined:   { rail: '#dc2626', icon: XCircle },
  offer_missed:     { rail: '#6b7280', icon: Clock },
  returned:         { rail: '#d97706', icon: Undo2 },
};
const STATUS_ARTEN = new Set(['online', 'offline']);

// Eine Tagesliste (neueste zuerst) in Blöcke schneiden: Statuswechsel stehen für sich,
// alles dazwischen wird zu einem aufklappbaren Fahrten-Block zusammengefasst.
function inBloecke(ereignisse) {
  const bloecke = [];
  let lauf = [];
  const abschliessen = () => { if (lauf.length) { bloecke.push({ art: 'lauf', ereignisse: lauf }); lauf = []; } };
  for (const ereignis of ereignisse) {
    if (STATUS_ARTEN.has(ereignis.type)) { abschliessen(); bloecke.push({ art: 'status', ereignis }); }
    else lauf.push(ereignis);
  }
  abschliessen();
  return bloecke;
}

// Überschrift eines Blocks — zählt, was wirklich drinsteht, statt etwas zu behaupten.
function blockTitel(ereignisse) {
  const fahrten = new Set(ereignisse.filter((e) => e.type === 'accepted').map((e) => e.ride_id)).size;
  const angebote = ereignisse.filter((e) => e.type === 'offer_declined' || e.type === 'offer_missed').length;
  const teile = [];
  if (fahrten > 0) teile.push(`Angenommen · ${fahrten} ${fahrten === 1 ? 'Fahrt' : 'Fahrten'}`);
  if (angebote > 0) teile.push(`${angebote} ${angebote === 1 ? 'Angebot' : 'Angebote'} ohne Fahrt`);
  return teile.join(' · ') || 'Aktivität';
}

function Knoten({ art, letzter }) {
  const stil = EREIGNIS[art] || { rail: '#6b7280', icon: CircleDot };
  const Icon = stil.icon;
  return (
    <div style={{ position: 'relative', width: 26, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
      {/* Verbindungslinie nach unten — beim letzten Eintrag entfällt sie. */}
      {!letzter && <span aria-hidden="true" style={{ position: 'absolute', top: 24, bottom: -18, left: 12, width: 2, background: '#e5e7eb' }} />}
      <span style={{
        position: 'relative', width: stil.betont ? 26 : 22, height: stil.betont ? 26 : 22, borderRadius: '50%',
        background: stil.stark || stil.betont ? stil.rail : '#fff',
        border: `2px solid ${stil.rail}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: stil.stark || stil.betont ? '#fff' : stil.rail, flexShrink: 0,
      }}>
        <Icon size={stil.betont ? 13 : 11} />
      </span>
    </div>
  );
}

function Zeile({ ereignis, letzter }) {
  const stil = EREIGNIS[ereignis.type] || {};
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '7px 0' }}>
        <Knoten art={ereignis.type} letzter={letzter} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: stil.stark || stil.betont ? 800 : 600, color: '#0a0a0a' }}>
            {ereignis.label}
          </div>
          {ereignis.detail && (
            <div style={{ fontSize: 12, color: '#4b5563', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {ereignis.detail}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
            {uhrzeit(ereignis.time)}
            {/* Altbestand ohne eigenen Zeitstempel: die Zeit wird als ungefähr AUSGEWIESEN,
                statt eine falsche Uhrzeit als Tatsache zu zeigen. */}
            {ereignis.time_is_approximate && (
              <span title="Für diese Fahrt wurde damals kein eigener Zeitpunkt gespeichert — angezeigt wird die Bestellzeit."
                style={{ marginLeft: 4, color: '#6b7280' }}>≈</span>
            )}
          </div>
        </div>
      </div>
      {/* Abstand zum nächstälteren Schritt — auf der Linie eingerückt, wie im Vorbild. */}
      {ereignis.gap_seconds != null && !letzter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '2px 0 6px' }}>
          <div style={{ width: 26, flexShrink: 0 }} />
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 12, color: '#6b7280' }}>{dauer(ereignis.gap_seconds)}</div>
        </div>
      )}
    </div>
  );
}

function ActivityTab({ id, range, setRange }) {
  const [act, setAct] = useState(null);
  const [ladend, setLadend] = useState(true);
  const [fehler, setFehler] = useState(false);
  const [eingeklappt, setEingeklappt] = useState({});

  const laden = useCallback(() => {
    setLadend(true); setFehler(false);
    api.get(`/partners/driver/${id}/activity`, { params: { from: range.from, to: range.to } })
      .then((r) => { setAct(r.data); setFehler(false); })
      // Ein Fehler darf nicht als leerer Platzhalter enden — vorher blieb hier ewig ein
      // Ladebalken stehen, ohne Meldung und ohne Weg zurück.
      .catch(() => setFehler(true))
      .finally(() => setLadend(false));
  }, [id, range]);
  useEffect(() => { laden(); }, [laden]);

  const { tage, ersterLauf } = useMemo(() => {
    const gruppen = new Map();
    for (const ereignis of (act?.events || [])) {
      const schluessel = betriebstagSchluessel(ereignis.time);
      if (!gruppen.has(schluessel)) gruppen.set(schluessel, []);
      gruppen.get(schluessel).push(ereignis);
    }
    // Jeder Fahrten-Block bekommt hier seinen stabilen Schluessel. Offen ist standardmaessig
    // der JUENGSTE Fahrten-Block — nicht „Block 0": davor steht in aller Regel ein
    // Statuswechsel (Offline), und der ist selbst kein aufklappbarer Block.
    let ersterLauf = null;
    const tageMitBloecken = [...gruppen.entries()].map(([schluessel, ereignisse]) => ({
      schluessel,
      ereignisse,
      bloecke: inBloecke(ereignisse).map((block, i) => {
        if (block.art !== 'lauf') return block;
        const blockSchluessel = `${schluessel}-${i}`;
        if (ersterLauf === null) ersterLauf = blockSchluessel;
        return { ...block, schluessel: blockSchluessel };
      }),
    }));
    return { tage: tageMitBloecken, ersterLauf };
  }, [act]);

  const online = act?.current_status === 'online';

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20,
          background: online ? '#faf5fb' : '#f3f4f6', fontSize: 12, fontWeight: 700,
          color: online ? '#9C27B0' : '#4b5563',
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: online ? '#9C27B0' : '#6b7280' }} />
          Aktueller Status: {online ? 'Online' : 'Offline'}
        </div>
        {/* Zeitraum wie in den Nachbarreitern — vorher waren es fest sieben Tage. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" aria-label="Von" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            style={{ padding: '7px 10px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 12 }} />
          <span style={{ color: '#6b7280' }}>–</span>
          <input type="date" aria-label="Bis" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            style={{ padding: '7px 10px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 12 }} />
        </div>
      </div>

      {fehler && (
        <div role="status" data-testid="aktivitaet-fehler" style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', marginBottom: 16,
          borderRadius: 12, background: '#fff', border: '1px solid #fecaca', fontSize: 13, color: '#0a0a0a',
        }}>
          <AlertTriangle size={16} color="#dc2626" style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>Die Aktivität konnte nicht geladen werden.</span>
          <button type="button" onClick={laden} style={{
            flexShrink: 0, padding: '6px 12px', borderRadius: 8, border: 'none',
            background: '#0a0a0a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>Erneut versuchen</button>
        </div>
      )}

      {ladend && !act && !fehler && <Skeleton h={80} r={10} />}

      {!ladend && !fehler && tage.length === 0 && (
        <div style={{ color: '#4b5563', padding: 30, textAlign: 'center', fontSize: 13 }}>
          In diesem Zeitraum gibt es keine Aktivität.
        </div>
      )}

      {tage.map((tag, tagIndex) => (
        <div key={tag.schluessel} style={{ marginBottom: 26 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#374151', marginBottom: 10 }}>
            {tagUeberschrift(tag.ereignisse[0].time)}
          </div>
          {tag.bloecke.map((block, blockIndex) => {
            const letzterBlock = tagIndex === tage.length - 1 && blockIndex === tag.bloecke.length - 1;
            if (block.art === 'status') {
              return (
                <Zeile key={`s-${block.ereignis.time}-${block.ereignis.type}`}
                  ereignis={block.ereignis}
                  letzter={letzterBlock} />
              );
            }
            const { schluessel } = block;
            // Der jüngste Fahrten-Block steht offen, ältere sind eingeklappt — wie im Vorbild.
            const zu = eingeklappt[schluessel] ?? (schluessel !== ersterLauf);
            // Die Uhrzeit der Überschrift ist die ERSTE Annahme des Blocks — der Moment, in dem
            // die Arbeit begann. Nur wenn es keine Annahme gibt (reiner Angebots-Block), steht
            // dort das älteste Ereignis. Sonst datiert „Angenommen · 2 Fahrten" auf ein
            // abgelehntes Angebot, also auf etwas, das gerade NICHT angenommen wurde.
            const annahmen = block.ereignisse.filter((e) => e.type === 'accepted');
            const aeltestes = (annahmen.length ? annahmen : block.ereignisse).at(-1);
            return (
              <div key={schluessel}>
                <button type="button"
                  onClick={() => setEingeklappt((c) => ({ ...c, [schluessel]: !zu }))}
                  aria-expanded={!zu}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '7px 0',
                    background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                  }}>
                  <div style={{ width: 26, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 800, color: '#0a0a0a' }}>
                    {blockTitel(block.ereignisse)}
                  </span>
                  <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{uhrzeit(aeltestes.time)}</span>
                  {zu ? <ChevronDown size={16} color="#4b5563" /> : <ChevronUp size={16} color="#4b5563" />}
                </button>
                {!zu && block.ereignisse.map((ereignis, i) => (
                  <Zeile key={`${ereignis.type}-${ereignis.time}-${ereignis.ride_id || i}`}
                    ereignis={ereignis}
                    letzter={letzterBlock && i === block.ereignisse.length - 1} />
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
